import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useToast } from 'components/hooks/useToast';
import { EditorState } from 'draft-js';
import { Macros } from 'interfaces/Macros';
import { useDispatch } from 'react-redux';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { competencyDetailsAdapter } from './adapters/competencyDetails';
import {
  incrementCommentOffset,
  incrementOffset,
  pushToComments,
  pushToPosts,
  setAllCommentsFetched,
  setAllPostsFetched,
  setComment,
  setCommentOffset,
  setComments,
  setOffset,
  setPosts,
  setProgressScore,
  setUploadedGif,
  setUploadedImage,
  useCompetencyDrawerState,
} from './competencyDrawerSlice';
import { useCompetencyQueryParams } from './helpers/useCompetencyQueryParams';
import { CompetencyDetailsRaw } from './types/competencyDetails';
import { CompetencyDetails } from './types/competencyDetailsAdapter';
import { GetLevelUpDetailsArgs } from './types/getCompetencyDrawerData';
import { FeedbackType } from './types/postsRaw';
import { CreateTaskPayload } from './types/tasks';
import { TaskRaw } from './types/tasksRaw';

const getLevelUpDetails = async ({
  employeeId,
  competencyId,
}: GetLevelUpDetailsArgs): Promise<{ entity: CompetencyDetailsRaw }> => {
  const url = endpoints.competency.competencyScore;
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: {
      competencyId,
      employeeId,
    },
  });
};

export const useGetLevelUpDetailsQuery = () => {
  const { competencyId, employeeId } = useCompetencyQueryParams();

  return useQuery<CompetencyDetails>(
    [REACT_QUERY_KEYS.competencyDrawerNew.levelUpDetails, employeeId, competencyId],
    () =>
      getLevelUpDetails({
        employeeId,
        competencyId,
      }).then((res) => competencyDetailsAdapter(res.entity)),
    { enabled: !!employeeId && !!competencyId, staleTime: 10 * 1000 }
  );
};

const getComments = ({
  competencyId,
  employeeId,
  limit,
  offset,
}: {
  competencyId: number | string;
  employeeId: number | string;
  limit: number;
  offset: number;
}): Promise<{ entity: FeedbackType[] }> => {
  const url = endpoints.competency.comments;
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: {
      competencyId,
      employeeId,
      limit,
      offset,
    },
  });
};

export const useGetCommentsMutation = () => {
  const { competencyId, employeeId } = useCompetencyQueryParams();

  const dispatch = useDispatch();

  return useMutation<
    FeedbackType[],
    unknown,
    { limit: number; offset: number; isResetComments?: boolean; newOffset?: number }
  >(
    ({ limit, offset }) =>
      getComments({
        competencyId,
        employeeId,
        limit,
        offset,
      }).then((res) => res.entity),
    {
      onSuccess: (data, { isResetComments, newOffset = 3 }) => {
        if (data?.length > 0) {
          if (isResetComments) {
            dispatch(setComments(data));
            dispatch(setCommentOffset(newOffset));
          } else {
            dispatch(pushToComments(data));
            dispatch(incrementCommentOffset(3));
          }
          if (data.length < 3) {
            dispatch(setAllCommentsFetched(true));
          }
        } else if (data?.length === 0) {
          dispatch(setAllCommentsFetched(true));
        }
      },
    }
  );
};

const updateComment = ({
  employeeId,
  competencyId,
  comment,
  commentId,
}: {
  employeeId: number;
  competencyId: number;
  comment: { text: string; macros: Macros };
  commentId: string;
}) => {
  const url = endpoints.competency.updateComment(commentId);
  return sendRequest({
    url,
    method: 'PUT',
    credentials: 'include',
    body: { ...comment, uuid: commentId, visibilityType: 'GLOBAL', type: 'COMMENT' },
    queryParams: {
      competencyId,
      employeeId,
    },
  });
};

export const useUpdateCommentMutation = () => {
  const { competencyId, employeeId } = useCompetencyQueryParams();

  const { mutate: fetchComments } = useGetCommentsMutation();

  const { commentsOffset } = useCompetencyDrawerState();

  const toast = useToast();

  return useMutation<
    { message: string },
    { message?: string },
    { comment: { text: string; macros: Macros }; commentId: string }
  >(
    ({ comment, commentId }) =>
      updateComment({
        employeeId: Number(employeeId),
        competencyId: Number(competencyId),
        comment,
        commentId,
      }),
    {
      onSuccess: (data) => {
        toast({
          title: data.message,
        });
        fetchComments({
          limit: commentsOffset,
          offset: 0,
          isResetComments: true,
          newOffset: commentsOffset,
        });
      },
      onError: (data) => {
        toast({
          title: data?.message || 'An unknown error has occurred, please try again',
          status: 'error',
        });
      },
    }
  );
};

const updateCompetencyProgress = ({
  employeeId,
  competencyId,
  comment,
  progressId,
}: {
  employeeId: number;
  competencyId: number;
  comment: { text: string; macros: Macros };
  progressId: number;
}) => {
  const url = endpoints.competency.checkin;
  return sendRequest({
    url,
    method: 'PUT',
    credentials: 'include',
    body: {
      employeeId,
      competencyId,
      comment,
      labelId: progressId,
    },
    queryParams: undefined,
  });
};

export const useUpdateCompetencyProgressMutation = () => {
  const queryClient = useQueryClient();

  const { competencyId, employeeId } = useCompetencyQueryParams();

  const { mutate: fetchComments } = useGetCommentsMutation();

  const dispatch = useDispatch();

  const toast = useToast();

  return useMutation<
    { message: string },
    { message?: string },
    { progressId: number; comment: { text: string; macros: Macros } }
  >(
    ({ progressId, comment }) =>
      updateCompetencyProgress({
        employeeId: Number(employeeId),
        competencyId: Number(competencyId),
        progressId,
        comment,
      }),
    {
      onSuccess: (data) => {
        toast({
          title: data.message,
        });
        queryClient.invalidateQueries([REACT_QUERY_KEYS.competencyDrawerNew.levelUpDetails]);
        queryClient.invalidateQueries([REACT_QUERY_KEYS.competencyManage.myCompetencies]);
        queryClient.invalidateQueries([REACT_QUERY_KEYS.mySummary.mySummaryCompetency]);
        dispatch(setComment(EditorState.createEmpty()));
        dispatch(setUploadedImage(null));
        dispatch(setUploadedGif(null));
        dispatch(setAllCommentsFetched(false));
        fetchComments({
          limit: 3,
          offset: 0,
          isResetComments: true,
        });
      },
      onError: (data) => {
        toast({
          title: data?.message || 'An unknown error has occurred, please try again',
          status: 'error',
        });
      },
    }
  );
};

const getPosts = async ({
  employeeId,
  feedbackType,
  limit,
  offset,
  competencyId,
}: {
  employeeId: string;
  feedbackType: 'FEEDBACK' | 'KUDOS';
  limit: number;
  offset: number;
  competencyId: string | number;
}): Promise<{ entity: FeedbackType[] }> => {
  const url = endpoints.developments.feedbackV1(employeeId, competencyId);
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: {
      feedback_type: feedbackType,
      limit,
      offset,
    },
  });
};

export const useGetPostsMutation = () => {
  const { competencyId, employeeId } = useCompetencyQueryParams();

  const dispatch = useDispatch();

  return useMutation<
    FeedbackType[],
    unknown,
    { feedbackType: 'FEEDBACK' | 'KUDOS'; limit: number; offset: number; isResetPosts?: boolean }
  >(
    ({ feedbackType, limit, offset }) =>
      getPosts({
        employeeId,
        feedbackType,
        limit,
        offset,
        competencyId,
      }).then((res) => res.entity),
    {
      onSuccess: (data, { isResetPosts, feedbackType }) => {
        const sliceFeedbackType = feedbackType === 'KUDOS' ? 'praise' : 'feedback';
        if (data?.length > 0) {
          if (isResetPosts) {
            dispatch(setPosts({ feedbackType: sliceFeedbackType, posts: data }));
            dispatch(setOffset({ feedbackType: sliceFeedbackType, offset: 3 }));
            dispatch(setAllPostsFetched({ feedbackType: sliceFeedbackType, isAllPostsFetched: false }));
          } else {
            dispatch(pushToPosts({ feedbackType: sliceFeedbackType, posts: data }));
            dispatch(incrementOffset({ feedbackType: sliceFeedbackType, offset: 3 }));
            if (data.length < 3) {
              dispatch(setAllPostsFetched({ feedbackType: sliceFeedbackType, isAllPostsFetched: true }));
            }
          }
        } else if (data?.length === 0) {
          dispatch(setAllPostsFetched({ feedbackType: sliceFeedbackType, isAllPostsFetched: true }));
        }
      },
    }
  );
};

const getCompetencyScore = async ({
  employeeId,
  competencyId,
  progressId,
}: {
  employeeId: string;
  competencyId: string;
  progressId: number;
}): Promise<{ entity: number }> => {
  const url = endpoints.competency.competencyUpdatedScore;
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: {
      employeeId,
      competencyId,
      labelId: progressId,
    },
  });
};

export const useGetCompetencyScoreMutation = () => {
  const { competencyId, employeeId } = useCompetencyQueryParams();

  const dispatch = useDispatch();

  return useMutation<number, unknown, { progressId: number }>(
    ({ progressId }) =>
      getCompetencyScore({
        employeeId,
        competencyId,
        progressId,
      }).then((res) => res.entity),
    {
      onSuccess: (score) => {
        dispatch(setProgressScore(score));
      },
    }
  );
};

export const getCompetencyTasks = ({
  employeeId,
  competencyId,
}: {
  employeeId: string;
  competencyId: string;
}): Promise<{ entity: TaskRaw[] }> => {
  const url = endpoints.competency.task;
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: {
      employeeId,
      competencyId,
    },
  });
};

export const useGetCompetencyTasksQuery = () => {
  const { competencyId, employeeId } = useCompetencyQueryParams();

  return useQuery<TaskRaw[]>(
    [REACT_QUERY_KEYS.competencyDrawerNew.competencyTasks, employeeId, competencyId],
    () =>
      getCompetencyTasks({
        employeeId,
        competencyId,
      }).then((res) => res.entity),
    { enabled: !!employeeId && !!competencyId, staleTime: 10 * 1000 }
  );
};

export const useCreateCompetencyTaskMutation = () => {
  const queryClient = useQueryClient();

  const { competencyId, employeeId } = useCompetencyQueryParams();

  return useMutation<unknown, unknown, Partial<CreateTaskPayload>>(
    ({ text, toIds, dueTS, startTS, description }) =>
      sendRequest({
        url: endpoints.competency.createTask,
        method: 'POST',
        credentials: 'include',
        body: {
          text,
          to: null,
          macros: {
            employees: {},
            demographics: {},
            attributes: {
              orgTag: {},
              money: null,
            },
          },
          from: null,
          dueDate: null,
          dueTS,
          teams: [],
          toIds,
          taskType: 'COMPETENCY_TASK',
          parentPost: null,
          type: 'TASK',
          actual: 0,
          visibilityType: 'PRIVATE',
          startTS,
          description,
          tags: null,
          startingProgressValue: 0,
          weightage: 0,
          removedTeam: null,
          parentPosts: null,
        },
        queryParams: {
          competencyId,
          employeeId,
        },
      }).then((res) => res.entity),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([REACT_QUERY_KEYS.competencyDrawerNew.competencyTasks]);
      },
    }
  );
};
